import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { WEEKDAYS } from '@quire/shared';
import { api, type ExceptionRecord } from '../lib/api';
import { Empty, PageHeading, Spinner } from '../components/ui';
import { AddForm, AddToggles, type Disclosure } from '../components/AddRecord';
import { Confirm } from '../components/Confirm';
import {
  Ledger,
  LedgerToolbar,
  searchRows,
  type LedgerColumn,
  type LedgerGroup,
} from '../components/Ledger';
import { useListView } from '../lib/useListView';

function yearBounds(): { from: string; to: string } {
  const year = new Date().getUTCFullYear();
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

const month = (date: string) =>
  new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${date}T00:00:00Z`),
  );

function describe(date: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

function columnsFor(context: {
  nameOf: (classId: string | null) => string;
  /** Asks first; the panel waits on the promise. */
  onRemove: (row: ExceptionRecord) => Promise<unknown>;
  removing: boolean;
}): LedgerColumn<ExceptionRecord>[] {
  return [
    {
      id: 'date',
      header: 'Date',
      sort: (row) => row.date,
      numeric: true,
      search: (row) => describe(row.date),
      cell: (row) => (
        <>
          <span className="font-mono text-xs text-ink-soft tabular">{row.date}</span>
          <span className="ml-3">{describe(row.date)}</span>
        </>
      ),
    },
    {
      id: 'applies',
      header: 'Applies to',
      sort: (row) => context.nameOf(row.classId),
      search: (row) => context.nameOf(row.classId),
      cell: (row) =>
        row.classId ? (
          context.nameOf(row.classId)
        ) : (
          <span className="text-ink-soft">Every class</span>
        ),
    },
    {
      id: 'reason',
      header: 'Reason',
      sort: (row) => row.reason,
      search: (row) => row.reason,
      cell: (row) => row.reason,
    },
    {
      id: 'actions',
      header: 'Remove',
      headerHidden: true,
      align: 'right',
      cell: (row) => (
        <Confirm
          trigger="Remove"
          title="Remove this holiday?"
          description={`${describe(row.date)}, ${row.reason}, for ${context.nameOf(row.classId).toLowerCase()}. There is no undo: it would have to be typed in again, and the expected meetings it excused come back until it is.`}
          confirmLabel="Remove"
          pendingLabel="Removing…"
          disabled={context.removing}
          onConfirm={() => context.onRemove(row)}
          errorFallback="Could not remove that holiday."
        />
      ),
    },
  ];
}

/*
 * By month, and the order comes from the rows rather than a list here: the
 * server returns them by date, so the months arrive in the only sequence a
 * calendar has.
 */
const BY_MONTH: LedgerGroup<ExceptionRecord> = {
  of: (row) => row.date.slice(0, 7),
  label: (id, within) => (
    <>
      {month(`${id}-01`)}
      <span className="font-mono text-xs font-normal text-ink-soft tabular">{within.length}</span>
    </>
  ),
};

/**
 * Holidays and cancellations. A date listed here is dropped from the expected
 * meetings a class should have held, so attendance rates are not dented by a
 * week nobody was supposed to attend.
 */
export function ManageCalendar() {
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/app/manage/calendar' });
  const bounds = yearBounds();
  /*
   * The default range is a whole calendar year and moves with the clock, so it
   * stays in the component rather than in the route's schema: a schema default
   * that changes on New Year's Day would rewrite everybody's saved link.
   */
  const range = { from: search.from ?? bounds.from, to: search.to ?? bounds.to };
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set());
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [classId, setClassId] = useState<string>('');
  const [panel, setPanel] = useState<Disclosure>(null);

  const classes = useQuery({ queryKey: ['classes'], queryFn: api.classes.list });
  const exceptions = useQuery({
    queryKey: ['exceptions', range.from, range.to],
    queryFn: () => api.admin.exceptions({ from: range.from, to: range.to }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['exceptions'] });

  const create = useMutation({
    mutationFn: () =>
      api.admin.createException({ date, reason, classId: classId === '' ? null : classId }),
    onSuccess: async () => {
      setReason('');
      setPanel(null);
      await invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.admin.deleteException(id),
    onSuccess: invalidate,
  });

  const classNames = useMemo(
    () => new Map((classes.data?.classes ?? []).map((c) => [c.id, c.name])),
    [classes.data],
  );
  const columns = useMemo(
    () =>
      columnsFor({
        nameOf: (id) => (id ? (classNames.get(id) ?? 'One class') : 'Every class'),
        onRemove: (row) => remove.mutateAsync(row.id),
        removing: remove.isPending,
      }),
    [classNames, remove],
  );
  const view = useListView({ search, columns });

  const all = useMemo(() => exceptions.data?.exceptions ?? [], [exceptions.data]);
  const rows = useMemo(() => searchRows(all, columns, view.query), [all, columns, view.query]);

  return (
    <div>
      <PageHeading eyebrow="Days nobody is expected" title="Holidays and cancellations">
        <div className="flex flex-wrap items-end gap-2">
          <label className="grid gap-1">
            <span className="eyebrow">From</span>
            <input
              type="date"
              className="field w-auto font-mono"
              value={range.from}
              onChange={(e) => view.set({ from: e.target.value })}
            />
          </label>
          <label className="grid gap-1">
            <span className="eyebrow">To</span>
            <input
              type="date"
              className="field w-auto font-mono"
              value={range.to}
              onChange={(e) => view.set({ to: e.target.value })}
            />
          </label>
          <AddToggles panel={panel} onChange={setPanel} add="Add date" />
        </div>
      </PageHeading>

      <p className="mb-5 max-w-2xl text-sm text-ink-soft">
        A date listed here stops counting as a meeting the class was expected to hold, so a week
        nobody was meant to attend does not lower anyone's attendance rate. Registers can still be
        marked on these dates if something went ahead after all.
      </p>

      {panel === 'add' ? (
        <AddForm
          error={create.error}
          fallback="Could not add the date."
          pending={create.isPending}
          submit="Add date"
          onSubmit={() => create.mutate()}
        >
          <label className="grid gap-1.5">
            <span className="eyebrow">Date</span>
            <input
              type="date"
              className="field w-auto font-mono"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Applies to</span>
            <select
              className="field w-auto"
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
            >
              <option value="">Every class</option>
              {/*
              Active classes only. Setting aside a date for a class that has
              retired changes nothing, because it has no further sessions to
              excuse. The name lookup for the table below is built from the
              unfiltered list, so a closure recorded before a class retired
              still says which class it applied to rather than "One class".
            */}
              {(classes.data?.classes ?? [])
                .filter((klass) => klass.isActive)
                .map((klass) => (
                  <option key={klass.id} value={klass.id}>
                    {klass.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="grid gap-1.5 sm:col-span-2">
            <span className="eyebrow">Reason</span>
            <input
              className="field"
              required
              maxLength={200}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Christmas, half term, building closed…"
            />
          </label>
        </AddForm>
      ) : null}

      {exceptions.isLoading ? (
        <Spinner />
      ) : (
        <>
          <LedgerToolbar
            query={view.draft}
            onQuery={view.onQuery}
            searchPlaceholder="reason, date or class"
            shown={rows.length}
            total={all.length}
          />
          <Ledger
            caption="Holidays and cancellations"
            rows={rows}
            total={all.length}
            columns={columns}
            rowId={(row) => row.id}
            sort={view.sort}
            onSort={view.onSort}
            group={BY_MONTH}
            collapsed={shut}
            onCollapse={(id, closed) =>
              setShut((prev) => {
                const next = new Set(prev);
                if (closed) next.add(id);
                else next.delete(id);
                return next;
              })
            }
            empty={
              <Empty
                title="No dates set aside"
                hint="Add the holidays and closures for this range."
              />
            }
            noMatch={<Empty title="No match" hint="No closure in this range matches that." />}
          />
        </>
      )}

      <p className="mt-4 font-mono text-[11px] text-ink-faint">
        weeks run {WEEKDAYS[0]} to {WEEKDAYS[6]}
      </p>
    </div>
  );
}
